import { useState, useEffect } from 'react';
import { loadCanvasData, saveCanvasData, refreshCanvasData } from '../services/canvasService';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export const useCanvas = (googleUser) => {
    const [canvasEvents, setCanvasEvents] = useState([]);
    const [canvasCourses, setCanvasCourses] = useState([]);
    const [canvasUrl, setCanvasUrl] = useState('');
    const [showCanvasInput, setShowCanvasInput] = useState(true);
    const [firebaseReady, setFirebaseReady] = useState(false);

    // Wait for Firebase auth to be ready
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                console.log('Firebase auth ready, UID:', user.uid);
                setFirebaseReady(true);
            } else {
                console.log('No Firebase user');
                setFirebaseReady(false);
            }
        });

        return () => unsubscribe();
    }, []);

    // Load Canvas URL from Firebase when Firebase is ready
    useEffect(() => {
        if (!firebaseReady || !googleUser) {
            console.log('Not ready to load Canvas data. Firebase ready:', firebaseReady, 'Google user:', !!googleUser);
            return;
        }

        const loadData = async () => {
            console.log('Loading Canvas data from Firebase...');
            try {
                const data = await loadCanvasData();
                if (data) {
                    console.log('✅ Canvas data loaded:', data);
                    setCanvasUrl(data.url);
                    setCanvasEvents(data.events);
                    setCanvasCourses(data.courses || []);
                    setShowCanvasInput(false);

                    localStorage.setItem('canvasEvents', JSON.stringify(data.events));
                    localStorage.setItem('canvasCourses', JSON.stringify(data.courses || []));
                } else {
                    console.log('No Canvas data found in Firebase');
                }
            } catch (error) {
                console.error('Error loading Canvas data:', error);
            }
        };

        loadData();
    }, [firebaseReady, googleUser]);

    // Save Canvas URL to Firebase when it changes
    useEffect(() => {
        if (!firebaseReady || !canvasUrl) return;

        const saveData = async () => {
            console.log('Saving Canvas URL to Firebase:', canvasUrl);
            await saveCanvasData(canvasUrl);
        };

        saveData();
    }, [canvasUrl, firebaseReady]);

    // Auto-refresh Canvas events every hour
    useEffect(() => {
        if (!googleUser || !canvasUrl) return;

        const refresh = async () => {
            console.log('Auto-refreshing Canvas events...');
            try {
                const { events, courses } = await refreshCanvasData(canvasUrl);
                setCanvasEvents(events);
                setCanvasCourses(courses || []);
                localStorage.setItem('canvasClasses', JSON.stringify(courses));
            } catch (error) {
                console.error('Error auto-refreshing Canvas events:', error);
            }
        };

        const interval = setInterval(refresh, 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, [googleUser, canvasUrl]);

    const handleFetchCanvasEvents = async () => {
        if (!canvasUrl) {
            alert('Please enter your Canvas calendar URL');
            return;
        }

        try {
            console.log('Fetching Canvas events for URL:', canvasUrl);
            const { events, courses } = await refreshCanvasData(canvasUrl);
            setCanvasEvents(events);
            setCanvasCourses(courses || []);
            setShowCanvasInput(false);
            await saveCanvasData(canvasUrl);
            console.log('✅ Canvas events fetched and saved');
        } catch (error) {
            console.error('Error fetching Canvas events:', error);
            alert('Failed to fetch Canvas events. Please try again.');
        }
    };

    const handleRefresh = async () => {
        if (!canvasUrl) {
            alert('No Canvas URL configured');
            return;
        }

        try {
            console.log('Manually refreshing Canvas events...');
            const { events, courses } = await refreshCanvasData(canvasUrl);
            setCanvasEvents(events);
            setCanvasCourses(courses || []);
            console.log('✅ Canvas events refreshed');
        } catch (error) {
            console.error('Error refreshing Canvas events:', error);
            alert('Failed to refresh Canvas events. Please try again.');
        }
    };

    return {
        canvasEvents,
        canvasCourses,
        canvasUrl,
        setCanvasUrl,
        showCanvasInput,
        handleFetchCanvasEvents,
        handleRefresh
    };
};